import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import SVG from 'react-inlinesvg';
import SideActionBoard from '../../../../components/SideActionBoard';
import ChevronRightIcon from '../../../../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import { adminAPI } from '../../../../api';
import FormLoader from '../../../../components/FormLoader';
import useAlarm from '../../../../context/AlarmContext';
import FormInput from '../../../../components/FormInput';
import SelectInput from '../../../../components/SelectInput';

const CompanySidebar = ({ openSideActionBoard, setOpenSideActionBoard, onSuccess }) => {
  const { action, ids } = openSideActionBoard;
  const id = ids[0];
  const isEdit = action === 'edit';

  const { setAlarm } = useAlarm();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    line_one: '',
    line_two: '',
    city: '',
    country_id: '',
    state_id: '',
    zip: '',
    phone: '',
    company_notes: '',
    cra_client_captain: '',
    cohort_ids: [],
  });

  const { data: metadata } = useQuery(
    'getCompaniesMetadata',
    adminAPI.getCompaniesMetadata
  );

  const { data: company, refetch } = useQuery(
    ['getCompanyDetails', { id: id || '' }],
    adminAPI.getCompanyDetails,
    {
      enabled: isEdit,
    }
  );

  useEffect(() => {
    if (isEdit && id) {
      refetch();
    }
  }, [id, isEdit, refetch]);

  useEffect(() => {
    if (isEdit && company) {
      setFormData({
        name: company.name || '',
        line_one: company.line_one || '',
        line_two: company.line_two || '',
        city: company.city || '',
        country_id: company.country_id || '',
        state_id: company.state_id || '',
        zip: company.zip || '',
        phone: company.phone || '',
        company_notes: company.company_notes || '',
        cra_client_captain: company.cra_client_captain || '',
        cohort_ids: company.cohort_ids || [],
      });
    }
  }, [company, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'country_id') {
        updated.state_id = ''; // reset state when country changes
      }
      return updated;
    });
  };

  const handleCohortCheck = (checked, cohortId) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        cohort_ids: [...prev.cohort_ids, cohortId],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        cohort_ids: prev.cohort_ids.filter((cid) => cid !== cohortId),
      }));
    }
  };

  const { mutate: createCompanyMutation, isLoading: createLoading } = useMutation(
    adminAPI.createCompany,
    {
      onSuccess: () => {
        setOpenSideActionBoard(null);
        setAlarm({ type: 'success', message: 'Company created successfully' });
        queryClient.invalidateQueries('getAllCompanies');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage = errors?.length > 0 ? errors.join(', ') : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    }
  );

  const { mutate: updateCompanyMutation, isLoading: updateLoading } = useMutation(
    adminAPI.updateCompany,
    {
      onSuccess: () => {
        setOpenSideActionBoard(null);
        setAlarm({ type: 'success', message: 'Company updated successfully' });
        queryClient.invalidateQueries('getAllCompanies');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage = errors?.length > 0 ? errors.join(', ') : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      updateCompanyMutation({ id, data: formData });
    } else {
      createCompanyMutation(formData);
    }
  };

  const countryOptions = [
    { value: '', label: '' },
    ...(metadata?.countries || []),
  ];

  const stateOptions = [
    { value: '', label: '' },
    ...(metadata?.states || []).filter(
      (s) => String(s.country_id) === String(formData.country_id)
    ),
  ];

  const cohorts = metadata?.cohorts || [];

  return (
    <SideActionBoard setOpenSideActionBoard={() => setOpenSideActionBoard(null)}>
      <div className="flex justify-between items-center relative pb-4 px-8">
        <button
          className="p-2 flex items-center -ml-2"
          type="button"
          onClick={() => setOpenSideActionBoard(null)}
        >
          <SVG src={ChevronRightIcon} />
        </button>
        <span
          style={{ width: 'calc(100% + 6rem)' }}
          className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
        />
      </div>

      <form className="overflow-y-scroll h-full px-8 py-8" onSubmit={handleSubmit}>
        <h1>{isEdit ? 'Edit Company' : 'Create Company'}</h1>

        <div style={{ gap: '16px' }} className="flex flex-col my-12">
          <div className="mb-4">
            <FormInput
              label="Name *"
              name="name"
              value={formData.name}
              type="text"
              handleChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="Address Line 1"
              name="line_one"
              value={formData.line_one}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="Address Line 2"
              name="line_two"
              value={formData.line_two}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="City"
              name="city"
              value={formData.city}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <p className="text-sm text-charcoal font-bold pb-2">Country</p>
            <SelectInput
              name="country_id"
              value={formData.country_id}
              options={countryOptions}
              onChange={handleSelectChange}
            />
          </div>

          <div className="mb-4">
            <p className="text-sm text-charcoal font-bold pb-2">State</p>
            <SelectInput
              name="state_id"
              value={formData.state_id}
              options={stateOptions}
              onChange={handleSelectChange}
              disabled={!formData.country_id}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="Zip"
              name="zip"
              value={formData.zip}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="Phone"
              name="phone"
              value={formData.phone}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="CRA Client Captain"
              name="cra_client_captain"
              value={formData.cra_client_captain}
              type="text"
              handleChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="font-semibold text-gray-700">Company Notes</label>
            <textarea
              name="company_notes"
              value={formData.company_notes}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              rows="4"
            />
          </div>

          <div className="pb-2">
            <p className="text-sm text-charcoal font-bold pb-2">Associated Cohorts</p>
            <div className="rounded-lg border-2 border-gray p-2 relative overflow-hidden" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {cohorts.length > 0 ? (
                cohorts.map((cohort) => {
                  const isChecked = formData.cohort_ids.includes(cohort.value);
                  return (
                    <div className="px-4 py-2 flex items-center" key={cohort.value}>
                      <input
                        id={`cohort-${cohort.value}`}
                        className="mr-4"
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleCohortCheck(e.target.checked, cohort.value)}
                      />
                      <label htmlFor={`cohort-${cohort.value}`}>{cohort.value} - {cohort.label}</label>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 text-gray-500">No cohorts available</div>
              )}
            </div>
          </div>
        </div>

        <button
          className="mb-6 p-4 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          type="submit"
          disabled={!formData.name.trim()}
        >
          {isEdit ? 'Update Company' : 'Create Company'}
        </button>

        {(createLoading || updateLoading) && <FormLoader />}
      </form>
    </SideActionBoard>
  );
};

export default CompanySidebar;
